import React, { useMemo, useRef, useState } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";
import { getPagosProfesionalesByFecha } from "../../../api/pagosApi";
import { formatMiles as formatMonto } from "../../../utils/miles";

function DefaultColumnFilter({ column: { filterValue, setFilter, placeholder } }) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={placeholder || "Filtrar..."}
      style={{ minWidth: 100 }}
    />
  );
}

export default function PagosProfesionales() {
  const [data, setData] = useState([]);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [loading, setLoading] = useState(false);
  const tableRef = useRef(null);

  const columns = useMemo(
    () => [
      {
        Header: "Fecha Orden",
        accessor: "fechaordenpago",
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        Cell: ({ value }) => (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{value ?? ""}</span>
        ),
      },
      {
        Header: "Profesional",
        accessor: "nombre",
        minWidth: 220,
        Cell: ({ value }) => (
          <div style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
            {value ?? ""}
          </div>
        ),
      },
      {
        Header: "N° Factura",
        accessor: "numerofactura",
        width: 160,
        minWidth: 130,
        maxWidth: 200,
      },
      {
        Header: "Método Pago",
        accessor: "metododepago",
        width: 140,
        minWidth: 120,
        maxWidth: 180,
        Cell: ({ value }) => (
          <span>{value ?? <span style={{ color: "#9aa1a8" }}>-</span>}</span>
        ),
      },
      {
        Header: "id Liq.",
        accessor: "idliquidacion",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        disableGlobalFilter: true,
        Cell: ({ value }) =>
          value ? (
            <div className="text-center">
              <Link
                className="pill-link"
                to={`/fichaliquidacion/${value}`}
                title={`Ver Liquidación ${value}`}
              >
                {value}
              </Link>
            </div>
          ) : (
            <div className="text-center" style={{ color: "#9aa1a8" }}>-</div>
          ),
      },
      {
        Header: "id Orden",
        accessor: "idordendepago",
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        disableGlobalFilter: true,
        Cell: ({ value }) =>
          value ? (
            <div className="text-center">
              <Link
                className="pill-link pill-order"
                to={`/fichaorden/${value}`}
                title={`Ver Orden ${value}`}
              >
                {value}
              </Link>
            </div>
          ) : (
            <div className="text-center" style={{ color: "#9aa1a8" }}>-</div>
          ),
      },
      {
        Header: "Monto",
        accessor: "monto",
        width: 140,
        minWidth: 120,
        maxWidth: 160,
        Cell: ({ value }) => (
          <div style={{ textAlign: "right", fontWeight: 600 }}>
            {formatMonto(value)}
          </div>
        ),
      },
      {
        Header: "Estado",
        accessor: "estado",
        width: 130,
        minWidth: 120,
        maxWidth: 160,
      },
      {
        Header: "Comprobante",
        accessor: "comprobante",
        width: 130,
        minWidth: 110,
        maxWidth: 160,
        disableGlobalFilter: true,
        Cell: ({ value }) =>
          value ? (
            <div className="text-center">{value}</div>
          ) : (
            <div className="text-center" style={{ color: "#9aa1a8" }}>-</div>
          ),
      },
      {
        Header: "Fecha Creación",
        accessor: "fechacreacion",
        width: 160,
        minWidth: 140,
        maxWidth: 200,
        Cell: ({ value }) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "0.85rem", color: "#555" }}>
            {value ?? ""}
          </span>
        ),
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageSize: 15, pageIndex: 0 },
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    rows,
    gotoPage,
    pageCount,
    pageOptions,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  const handleBuscar = async () => {
    if (!fechaInicio || !fechaFin) {
      return Swal.fire({
        icon: "warning",
        title: "Faltan fechas",
        text: "Fecha inicio y Fecha fin son requeridas.",
      });
    }
    if (fechaInicio > fechaFin) {
      return Swal.fire({
        icon: "warning",
        title: "Rango inválido",
        text: "La fecha inicio no puede ser mayor que la fecha fin.",
      });
    }

    try {
      setLoading(true);
      const res = await getPagosProfesionalesByFecha({
        fechainicio: fechaInicio,
        fechafin: fechaFin,
      });
      setData(Array.isArray(res) ? res : []);
      gotoPage(0);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: `${err}` });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!rows || rows.length === 0) {
      return Swal.fire({
        icon: "info",
        title: "Sin datos",
        text: "No hay datos para exportar. Realizá una búsqueda primero.",
      });
    }

    const mapped = rows.map((r) => {
      const it = r.original;
      return {
        "ID Factura":        it.idfacturasprofesionales ?? "",
        "Fecha Orden Pago":  it.fechaordenpago ?? "",
        "Profesional":       it.nombre ?? "",
        "N° Factura":        it.numerofactura ?? "",
        "Método de Pago":    it.metododepago ?? "",
        "Id Liquidación":    it.idliquidacion ?? "",
        "Id Orden de Pago":  it.idordendepago ?? "",
        "Monto":             formatMonto(it.monto ?? 0),
        "Estado":            it.estado ?? "",
        "Comprobante":       it.comprobante ?? "",
        "Usuario Creación":  it.usuariocreacion ?? "",
        "Fecha Creación":    it.fechacreacion ?? "",
        "Usuario Edición":   it.usuarioedicion ?? "",
        "Fecha Edición":     it.fechaedicion ?? "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(mapped);

    // Ancho automático por columna
    const colWidths = Object.keys(mapped[0]).map((key) => ({
      wch: Math.max(key.length, ...mapped.map((r) => String(r[key] ?? "").length)) + 2,
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PagosProfesionales");
    XLSX.writeFile(wb, `PagosProfesionales_${fechaInicio}_a_${fechaFin}.xlsx`);
  };

  return (
    <div className="mt-3">
      <style>{`
        .tabla-scroll {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #fff;
        }
        .tabla-pagos {
          table-layout: fixed;
          min-width: 1200px;
          width: 100%;
          font-size: 0.92rem;
          border-collapse: separate;
          border-spacing: 0;
          white-space: nowrap;
        }
        .tabla-pagos thead tr.header-row th {
          position: sticky;
          top: 0;
          background: #1569c9ff;
          color: #fff;
          font-weight: 600;
          z-index: 2;
          border-bottom: none !important;
        }
        .tabla-pagos tbody tr:nth-child(even) { background: #fafafa; }
        .tabla-pagos tbody tr:hover { background: #f1f5f9; }
        .tabla-pagos th, .tabla-pagos td {
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
          padding: 8px 10px !important;
          border-color: #eef2f7 !important;
          height: 40px;
        }
        .pill-link {
          display: inline-block;
          padding: 2px 8px;
          font-size: 0.8rem;
          border-radius: 999px;
          background: #eef5ff;
          border: 1px solid #cfe0ff;
          color: #0b5ed7;
          text-decoration: none;
          text-align: center;
        }
        .pill-order {
          background: #f3fff1;
          border-color: #cfeccc;
          color: #2b7a0b;
        }
        .pill-comprobante {
          background: #fff8ee;
          border-color: #fcd9a0;
          color: #b45309;
        }
      `}</style>

      {/* Filtros */}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label mb-1">Fecha inicio</label>
          <input
            type="date"
            className="form-control"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label mb-1">Fecha fin</label>
          <input
            type="date"
            className="form-control"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </div>
        <div className="col-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleBuscar}
            disabled={!fechaInicio || !fechaFin || loading}
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={handleExportExcel}
            disabled={loading || rows.length === 0}
          >
            Descargar Excel
          </button>
        </div>
        <div className="col-auto ms-auto">
          <input
            type="text"
            className="form-control"
            placeholder="Buscar en todo…"
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "30vh" }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              border: "4px solid #ddd",
              borderTop: "4px solid #1569c9ff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>
            Cargando...
          </span>
          <style>{`@keyframes spin {0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
        </div>
      ) : (
        <div className="tabla-scroll">
          <table {...getTableProps()} className="table table-sm tabla-pagos" ref={tableRef}>
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <React.Fragment key={i}>
                  <tr {...headerGroup.getHeaderGroupProps()} className="header-row">
                    {headerGroup.headers.map((column) => (
                      <th key={column.id} {...column.getHeaderProps()}>
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                  <tr className="filter-row">
                    {headerGroup.headers.map((column) => (
                      <th key={`${column.id}-filter`}>
                        {column.canFilter ? column.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, ri) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={ri}>
                    {row.cells.map((cell, ci) => (
                      <td {...cell.getCellProps()} key={ci}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {page.length < 15 &&
                Array.from({ length: 15 - page.length }).map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td colSpan={columns.length}>&nbsp;</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      <div className="d-flex justify-content-between mt-2 align-items-center">
        <span>
          Página <strong>{pageIndex + 1} de {pageOptions.length || 1}</strong>
        </span>
        <div>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            {"<<"}
          </button>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Anterior
          </button>
          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => nextPage()} disabled={!canNextPage}>
            Siguiente
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
            {">>"}
          </button>
        </div>
      </div>
    </div>
  );
}